import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { LaseredCondition, LaseredConditionRequest } from "@/types";
import {
  reseedConditionWindows,
  sameConditionWindows,
  toYMD,
  type ConditionWindowDefaults,
  type ConditionWindows,
} from "@/utils/helpers/conditionWindowDefaults";
import { findDuplicateConditionName, validateLaseredCondition } from "@/utils/validations/laseredConditionValidation";
import { expectedTriggerReapplies, strandedExpectedTriggerWarning } from "@/utils/helpers/expectedTrigger";
import { laseredConditionApi, useLaseredConditions } from "./useLaseredConditions";

/** One editable conditional-laser row. `server` is the last saved state, if any. */
export interface ConditionDraft extends ConditionWindows {
  key: string;
  id?: string;
  lasered_condition_name: string;
  icd_codes: string[];
  cpt_codes: string[];
  amt: string;
  server?: LaseredCondition;
}

const NO_SAVED: LaseredCondition[] = [];

let draftSeq = 0;
const newKey = () => `draft-${++draftSeq}`;

const fromServer = (c: LaseredCondition): ConditionDraft => ({
  key: c.id,
  id: c.id,
  lasered_condition_name: c.lasered_condition_name,
  icd_codes: c.icd_codes ?? [],
  cpt_codes: c.cpt_codes ?? [],
  amt: String(c.amt_lasered_conditional_deductible ?? ""),
  incurred_start_date: toYMD(c.incurred_start_date),
  incurred_end_date: toYMD(c.incurred_end_date),
  paid_start_date: toYMD(c.paid_start_date),
  paid_end_date: toYMD(c.paid_end_date),
  server: c,
});

export const draftToRequest = (d: ConditionDraft): LaseredConditionRequest => ({
  lasered_condition_name: d.lasered_condition_name.trim(),
  icd_codes: d.icd_codes.map((s) => s.trim().toUpperCase()).filter(Boolean),
  cpt_codes: d.cpt_codes.map((s) => s.trim().toUpperCase()).filter(Boolean),
  amt_lasered_conditional_deductible: parseFloat(d.amt) || 0,
  incurred_start_date: d.incurred_start_date as LaseredConditionRequest["incurred_start_date"],
  incurred_end_date: d.incurred_end_date as LaseredConditionRequest["incurred_end_date"],
  paid_start_date: d.paid_start_date as LaseredConditionRequest["paid_start_date"],
  paid_end_date: d.paid_end_date as LaseredConditionRequest["paid_end_date"],
});

/** The row's terms match its last saved state (false for a new row). */
export const sameTerms = (d: ConditionDraft): boolean => {
  if (!d.server) return false;
  const a = draftToRequest(d);
  const s = d.server;
  const eq = (x: string[], y: string[]) => x.length === y.length && x.every((v, i) => v === y[i]);
  return (
    a.lasered_condition_name === s.lasered_condition_name &&
    eq(a.icd_codes, s.icd_codes ?? []) &&
    eq(a.cpt_codes, s.cpt_codes ?? []) &&
    a.amt_lasered_conditional_deductible === s.amt_lasered_conditional_deductible &&
    a.incurred_start_date === toYMD(s.incurred_start_date) &&
    a.incurred_end_date === toYMD(s.incurred_end_date) &&
    a.paid_start_date === toYMD(s.paid_start_date) &&
    a.paid_end_date === toYMD(s.paid_end_date)
  );
};

/** Server-derived fields of a condition (ignored on write). */
const derivedOf = (c: LaseredCondition): Partial<LaseredCondition> => ({
  lasered_condition_trigger_state: c.lasered_condition_trigger_state,
  trigger_date: c.trigger_date,
  triggered_by_claim_item_id: c.triggered_by_claim_item_id,
  is_effective: c.is_effective,
  expected_trigger: c.expected_trigger,
});

const sameDerived = (a: LaseredCondition, b: LaseredCondition): boolean =>
  JSON.stringify(derivedOf(a)) === JSON.stringify(derivedOf(b));

interface UseLaseredConditionDraftsArgs {
  policyId: string;
  /** Existing claimant whose saved conditions load as rows; undefined for a new claimant. */
  claimantId?: string;
  defaults: ConditionWindowDefaults;
  /** Load and reset when true (dialog open). */
  active: boolean;
}

/**
 * How many writes a failed `persist` / `removeAll` made before it threw (0 when
 * none), so the caller can say the save got part-way (!4880 thread 30678).
 */
export const writesBeforeFailure = (err: unknown): number => {
  const n = (err as { conditionWritesBeforeFailure?: unknown } | null)?.conditionWritesBeforeFailure;
  return typeof n === "number" ? n : 0;
};

const withWritesBeforeFailure = (err: unknown, written: number): unknown => {
  if (written > 0 && err && typeof err === "object") {
    (err as { conditionWritesBeforeFailure?: number }).conditionWritesBeforeFailure = written;
  }
  return err;
};

/**
 * Local rows for the conditional-laser editor plus a `persist` that writes them
 * once the claimant id is known (#3511). Writes go deletes → updates → creates,
 * so renaming one row to another's freed name works. A failure throws and leaves
 * the rows as they are, so the caller keeps the dialog open; rows already
 * written pick up their server state, so a retry does not duplicate them.
 */
export const useLaseredConditionDrafts = ({
  policyId,
  claimantId,
  defaults,
  active,
}: UseLaseredConditionDraftsArgs) => {
  const {
    conditions,
    isLoading,
    isFetching,
    isFetchedAfterMount,
    error: loadError,
    isUnsupported: unsupported,
    invalidate,
  } = useLaseredConditions(policyId, claimantId, active && !!claimantId);
  // `unsupported`: the API predates conditional lasers (the SPA can deploy
  // first). Nothing to load or write, and no reason to block the laser save.
  const [drafts, setDrafts] = useState<ConditionDraft[]>([]);
  const [removed, setRemoved] = useState<ConditionDraft[]>([]);
  // True while persist() is writing, so callers keep Save disabled until it settles.
  const [isPersisting, setIsPersisting] = useState(false);
  const loadedFor = useRef<string | undefined>(undefined);
  // Saved conditions removeAll() has deleted, so a retry after a part-way
  // failure does not delete one twice (the second would 404).
  const deletedIds = useRef<Set<string>>(new Set());
  // The open/claimant whose saved rows have been seeded (state, unlike
  // loadedFor, so `savedReady` re-renders).
  const [seededKey, setSeededKey] = useState<string | undefined>(undefined);

  // Seed rows from the server once per open/claimant, from a fetch that settled
  // after this opened, never from a cached (possibly stale) list or a failed
  // load. Rows the user added while the list was loading are kept; closing the
  // dialog discards everything.
  useEffect(() => {
    if (!active) {
      loadedFor.current = undefined;
      deletedIds.current = new Set();
      setSeededKey(undefined);
      setDrafts((prev) => (prev.length ? [] : prev));
      setRemoved((prev) => (prev.length ? [] : prev));
      return;
    }
    const key = `${policyId}:${claimantId ?? "new"}`;
    if (claimantId && (isFetching || !isFetchedAfterMount || loadError)) return;
    if (loadedFor.current === key) return;
    loadedFor.current = key;
    setSeededKey(key);
    const saved = claimantId ? conditions.map(fromServer) : [];
    setDrafts((prev) => [...saved, ...prev.filter((d) => !d.id)]);
    setRemoved([]);
  }, [active, policyId, claimantId, conditions, isFetching, isFetchedAfterMount, loadError]);

  // Later refetches (the recompute refresh after a write, an expected trigger
  // set or cancelled) bring new server-derived state: trigger, in force, the
  // expected trigger. Saved rows take it without touching what the user typed.
  // A saved row an earlier fetch returned and this one doesn't was deleted on the
  // server (another user): it goes, so its actions stop failing with 404 (!4881
  // thread 30687). A row this dialog just created is kept until a fetch has seen it.
  const fetchedIds = useRef(new Set<string>());
  useEffect(() => {
    if (!active || !claimantId || loadedFor.current !== `${policyId}:${claimantId}`) return;
    const byId = new Map(conditions.map((c) => [c.id, c]));
    const gone = new Set([...fetchedIds.current].filter((id) => !byId.has(id)));
    conditions.forEach((c) => fetchedIds.current.add(c.id));
    gone.forEach((id) => fetchedIds.current.delete(id));
    setDrafts((rows) => {
      let changed = false;
      const out = rows.flatMap((d) => {
        if (d.id && gone.has(d.id)) {
          changed = true;
          return [];
        }
        const fresh = d.id ? byId.get(d.id) : undefined;
        if (!fresh || !d.server || sameDerived(d.server, fresh)) return [d];
        changed = true;
        return [{ ...d, server: { ...d.server, ...derivedOf(fresh) } }];
      });
      return changed ? out : rows;
    });
  }, [active, policyId, claimantId, conditions]);

  // When the defaults move (policy switch in claimant create, policy term or
  // specific-benefit edits in policy create), unsaved rows follow them for every
  // window date the user has not changed. Saved rows keep their stored terms.
  const prevWindows = useRef(defaults.windows);
  useEffect(() => {
    const prev = prevWindows.current;
    const next = defaults.windows;
    prevWindows.current = next;
    if (sameConditionWindows(prev, next)) return;
    setDrafts((rows) => {
      let changed = false;
      const out = rows.map((d) => {
        if (d.id) return d;
        const r = reseedConditionWindows(d, prev, next);
        if (r !== d) changed = true;
        return r;
      });
      return changed ? out : rows;
    });
  }, [defaults.windows]);

  const add = useCallback(() => {
    setDrafts((prev) => [
      ...prev,
      { key: newKey(), lasered_condition_name: "", icd_codes: [], cpt_codes: [], amt: "", ...defaults.windows },
    ]);
  }, [defaults.windows]);

  const update = useCallback((key: string, patch: Partial<ConditionDraft>) => {
    setDrafts((prev) => prev.map((d) => (d.key === key ? { ...d, ...patch } : d)));
  }, []);

  const remove = useCallback((key: string) => {
    setDrafts((prev) => {
      const target = prev.find((d) => d.key === key);
      if (target?.id) setRemoved((r) => [...r, target]);
      return prev.filter((d) => d.key !== key);
    });
  }, []);

  /** A write outside `persist` (expected trigger, #7361) returned the row's new server state. */
  const applyServer = useCallback(
    (key: string, saved: LaseredCondition) => {
      setDrafts((prev) => prev.map((d) => (d.key === key ? { ...d, server: saved } : d)));
      invalidate();
    },
    [invalidate]
  );

  const rowErrors = useMemo(
    () =>
      Object.fromEntries(
        drafts.map((d) => [d.key, validateLaseredCondition({ ...d, amt_lasered_conditional_deductible: d.amt })])
      ) as Record<string, string>,
    [drafts]
  );

  const duplicateName = useMemo(
    () => findDuplicateConditionName(drafts.map((d) => d.lasered_condition_name)),
    [drafts]
  );

  const error = useMemo(() => {
    // Saving without the saved rows would look like deleting them; block it.
    if (claimantId && loadError && !unsupported) {
      return "Could not load this claimant's saved conditional lasers. Close and reopen to try again.";
    }
    if (duplicateName) return `Two conditional lasers are named "${duplicateName}". Names must be unique.`;
    // Only rows that will be written block a save: an unchanged saved row (e.g.
    // a legacy $0 deductible) must not stop removing another one.
    const first = drafts.find((d) => rowErrors[d.key] && !(d.id && sameTerms(d)));
    return first ? `${first.lasered_condition_name.trim() || "Conditional laser"}: ${rowErrors[first.key]}` : "";
  }, [drafts, rowErrors, duplicateName, claimantId, loadError, unsupported]);

  /** Rows `persist` would still write: removals plus new or changed rows. */
  const pendingWrites = removed.length + drafts.filter((d) => !d.id || !sameTerms(d)).length;
  const dirty = pendingWrites > 0;

  // Changed saved rows whose new windows no longer cover their live expected
  // trigger (#7361): the save asks first (PO 2026-10-07: warn).
  const strandedExpectedTriggers = useMemo(
    () =>
      drafts
        .filter((d) => d.id && !sameTerms(d))
        .map((d) => ({
          key: d.key,
          name: d.lasered_condition_name.trim() || d.server?.lasered_condition_name || "Conditional laser",
          warning: strandedExpectedTriggerWarning(d.server, d),
          // Brings a "Not applied" expected trigger back (PO 2026-10-14), rather than stranding one.
          reapplies: expectedTriggerReapplies(d.server, d),
        }))
        .filter((r) => !!r.warning),
    [drafts]
  );

  /** Write every change for `targetClaimantId`. Returns API advisories (e.g. inert). */
  const persist = useCallback(
    async (targetClaimantId: string): Promise<string[]> => {
      if (unsupported) return [];
      const warnings: string[] = [];
      let written = 0;
      setIsPersisting(true);
      try {
        for (const r of removed) {
          if (r.id) await laseredConditionApi.remove(targetClaimantId, r.id);
          setRemoved((prev) => prev.filter((x) => x.key !== r.key));
          written++;
        }
        for (const d of drafts) {
          if (d.id && sameTerms(d)) continue;
          const saved = d.id
            ? await laseredConditionApi.update(targetClaimantId, d.id, draftToRequest(d))
            : await laseredConditionApi.create(policyId, targetClaimantId, draftToRequest(d));
          if (saved.warning) warnings.push(saved.warning);
          setDrafts((prev) => prev.map((x) => (x.key === d.key ? { ...fromServer(saved), key: d.key } : x)));
          written++;
        }
      } catch (err) {
        throw withWritesBeforeFailure(err, written);
      } finally {
        setIsPersisting(false);
        // Also after a partial failure: rows already written changed server state.
        invalidate();
      }
      return [...new Set(warnings)];
    },
    [removed, drafts, policyId, invalidate, unsupported]
  );

  /**
   * Delete every saved conditional laser of `targetClaimantId` on the policy
   * (a change of laser type away from Conditional). Unsaved rows are dropped.
   * Throws on the first failure; the ones already deleted are not sent again.
   */
  const removeAll = useCallback(
    async (targetClaimantId: string): Promise<void> => {
      if (unsupported) return;
      const ids = new Set(
        [...conditions.map((c) => c.id), ...drafts.map((d) => d.id), ...removed.map((r) => r.id)].filter(
          (id): id is string => !!id
        )
      );
      let written = 0;
      setIsPersisting(true);
      try {
        for (const id of ids) {
          if (deletedIds.current.has(id)) continue;
          await laseredConditionApi.remove(targetClaimantId, id);
          deletedIds.current.add(id);
          written++;
        }
        setDrafts([]);
        setRemoved([]);
      } catch (err) {
        throw withWritesBeforeFailure(err, written);
      } finally {
        setIsPersisting(false);
        invalidate();
      }
    },
    [conditions, drafts, removed, invalidate, unsupported]
  );

  // The conditions on the server, once a fetch settled since this opened (the
  // rows are seeded from it); kept while a later refetch runs.
  const savedReady = unsupported || (active && seededKey === `${policyId}:${claimantId ?? "new"}`);
  const saved = savedReady && claimantId && !unsupported ? conditions : NO_SAVED;

  return {
    drafts,
    add,
    update,
    remove,
    rowErrors,
    error,
    dirty,
    /** How many conditional lasers are not saved yet (e.g. after a failed write). */
    pendingWrites,
    /** Rows this save writes whose new terms strand, or bring back, their expected trigger, with the warning. */
    strandedExpectedTriggers,
    persist,
    removeAll,
    applyServer,
    /** The claimant whose saved rows load (undefined for a new claimant). */
    claimantId,
    /** Saved (live) conditional lasers, once `savedReady`. */
    saved,
    /** The saved conditional lasers are known (loaded, or there is no claimant yet). */
    savedReady,
    isLoading,
    isPersisting,
    /** The saved rows failed to load; `error` blocks a save until they do. */
    loadFailed: !!claimantId && !!loadError && !unsupported,
    /** The API has no conditional-laser routes yet: hide the editor. */
    unsupported,
    /** Reload the saved rows (e.g. after an action the server refused as stale). */
    reload: invalidate,
  };
};

export type LaseredConditionDraftsApi = ReturnType<typeof useLaseredConditionDrafts>;
